import type { ReactNode } from 'react';
import { cn } from '../cn.js';

export interface NavLink {
  label: string;
  href: string;
}

export interface SiteHeaderProps {
  brand?: ReactNode;
  nav?: NavLink[];
  cta?: NavLink;
  className?: string;
}

export function SiteHeader({ brand = 'CartCloser', nav = [], cta, className }: SiteHeaderProps) {
  return (
    <header className={cn('border-b border-border', className)}>
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
        <a href="/" className="text-base font-semibold tracking-tight">
          {brand}
        </a>
        <nav className="flex items-center gap-6 text-sm">
          {nav.map((n) => (
            <a key={n.href} href={n.href} className="text-muted-foreground hover:text-foreground">
              {n.label}
            </a>
          ))}
          {cta ? (
            <a
              href={cta.href}
              className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {cta.label}
            </a>
          ) : null}
        </nav>
      </div>
    </header>
  );
}

export interface CTASectionProps {
  title: ReactNode;
  subtitle?: ReactNode;
  cta: NavLink;
  className?: string;
}

export function CTASection({ title, subtitle, cta, className }: CTASectionProps) {
  return (
    <section className={cn('mx-auto max-w-3xl px-6 py-20 text-center', className)}>
      <h2 className="text-3xl font-semibold tracking-tight">{title}</h2>
      {subtitle ? <p className="mt-3 text-muted-foreground">{subtitle}</p> : null}
      <a
        href={cta.href}
        className="mt-6 inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        {cta.label}
      </a>
    </section>
  );
}

export interface FooterColumn {
  title: string;
  links: NavLink[];
}

export function SiteFooter({
  columns = [],
  className,
}: {
  columns?: FooterColumn[];
  className?: string;
}) {
  return (
    <footer className={cn('border-t border-border', className)}>
      <div className="mx-auto max-w-5xl px-6 py-12">
        {columns.length > 0 ? (
          <div className="mb-8 grid gap-8 sm:grid-cols-2 md:grid-cols-4">
            <div>
              <p className="text-base font-semibold tracking-tight">CartCloser</p>
              <p className="mt-1 text-sm text-muted-foreground">The agentic conversion engine.</p>
            </div>
            {columns.map((col) => (
              <div key={col.title}>
                <p className="mb-2 text-sm font-semibold">{col.title}</p>
                <ul className="space-y-1.5">
                  {col.links.map((l) => (
                    <li key={l.href}>
                      <a href={l.href} className="text-sm text-muted-foreground hover:text-foreground">
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : null}
        <p className="text-sm text-muted-foreground">© 2026 CartCloser. All rights reserved.</p>
      </div>
    </footer>
  );
}
